import React, { useMemo } from 'react';

import { useIntl } from 'react-intl-next';

import { fg } from '@atlaskit/platform-feature-flags';

import { useAIAnswerContext } from '../../ai-answer-dialog/ai-answer-context';
import { SmartLinksAnalyticsContextLocation } from '../../ai-answer-dialog/analytics';
import {
	SainAction,
	useSAINUsageCriteriaMet,
} from '../../common/controller/analytics/hooks/use-sain-usage';
import { useAnalytics } from '../../common/controller/analytics/use-analytics';
import { messages } from '../../messages';
import { onKnowledgeCardLinkClick } from '../analytics';
import { KnowledgeCardActivityRow } from '../common/knowledge-card-activity-row';
import {
	type ActivityColumnConfig,
	KnowledgeCardBody,
	type TeamColumnConfig,
} from '../common/knowledge-card-body';
import { KnowledgeCardTeamsRow } from '../common/knowledge-card-teams-row';

import { type TeamsData } from './use-agent-card-data';

type AgentCardBodyProps = {
	currentUserId: string;
	teamsData?: TeamsData | null;
	activityData?: any;
	profileUrl: string;
	viewAllUrl?: string;
};

export const AgentCardBody: React.MemoExoticComponent<
	({
		currentUserId,
		teamsData,
		activityData,
		profileUrl,
		viewAllUrl,
	}: AgentCardBodyProps) => React.JSX.Element
> = React.memo(
	({
		currentUserId: _currentUserId,
		teamsData,
		activityData,
		profileUrl,
		viewAllUrl: _viewAllUrl,
	}: AgentCardBodyProps): React.JSX.Element => {
		const { commonAttributes } = useAIAnswerContext();
		const [fireAnalyticsEvent] = useAnalytics();
		const { fireSAINUsageEvent } = useSAINUsageCriteriaMet();
		const { formatMessage } = useIntl();

		const handleRecentWorkViewAllClick = useMemo(
			() => (e: React.MouseEvent) => {
				const isAuxClick = 'button' in e && e.button === 1;
				if (isAuxClick && !fg('kd_track_sain_middle_clicks')) {
					return;
				}
				fireAnalyticsEvent(
					onKnowledgeCardLinkClick({ ...commonAttributes, location: 'recent-work-view-all', isAuxClick }),
				);
				fireSAINUsageEvent({ metCriteria: SainAction.RECENT_WORK_CLICKED, commonAttributes });
			},
			[fireAnalyticsEvent, fireSAINUsageEvent, commonAttributes],
		);

		const handleAgentTeamsViewAllClick = useMemo(
			() => (e: React.MouseEvent) => {
				const isAuxClick = 'button' in e && e.button === 1;
				if (isAuxClick && !fg('kd_track_sain_middle_clicks')) {
					return;
				}
				fireAnalyticsEvent(
					onKnowledgeCardLinkClick({ ...commonAttributes, location: 'agent-teams-view-all', isAuxClick }),
				);
				fireSAINUsageEvent({ metCriteria: SainAction.TEAM_LIST_CLICKED, commonAttributes });
			},
			[fireAnalyticsEvent, fireSAINUsageEvent, commonAttributes],
		);

		// Determine which columns to render
		const hasRecentActivity = useMemo(
			() => activityData && Array.isArray(activityData) && activityData.length > 0,
			[activityData],
		);
		const hasTeams = useMemo(
			() => teamsData?.teamSearch?.nodes && teamsData.teamSearch.nodes.length > 0,
			[teamsData?.teamSearch?.nodes],
		);

		const columnsConfig = useMemo(
			() => [
				// Teams Column
				...(hasTeams && teamsData?.teamSearch?.nodes
					? [
							{
								viewAllLinkOnClick: handleAgentTeamsViewAllClick,
								viewAllLinkHref: profileUrl,
								data: teamsData.teamSearch.nodes,
								component: KnowledgeCardTeamsRow,
								header: formatMessage(messages.knowledge_cards_teams_subheader),
								onClickCallback: (position: number, shownAfterCardBodyExpanded: boolean, isAuxClick?: boolean) => {
									if (isAuxClick && !fg('kd_track_sain_middle_clicks')) {
										return;
									}
									fireAnalyticsEvent(
										onKnowledgeCardLinkClick({
											...commonAttributes,
											location: 'team-list-item',
											position,
											shownAfterCardBodyExpanded,
											isAuxClick,
										}),
									);
									fireSAINUsageEvent({
										metCriteria: SainAction.TEAM_LIST_ITEM_CLICKED,
										commonAttributes,
									});
								},
							} as unknown as TeamColumnConfig,
						]
					: []),
				// Recent Activity Column
				...(hasRecentActivity
					? [
							{
								viewAllLinkOnClick: handleRecentWorkViewAllClick,
								viewAllLinkHref: profileUrl,
								data: activityData,
								component: KnowledgeCardActivityRow,
								header: formatMessage(messages.knowledge_cards_worked_on_subheader),
								onClickCallback: (position: number, shownAfterCardBodyExpanded: boolean, isAuxClick?: boolean) => {
									if (isAuxClick && !fg('kd_track_sain_middle_clicks')) {
										return;
									}
									fireAnalyticsEvent(
										onKnowledgeCardLinkClick({
											...commonAttributes,
											location: 'recent-work-item',
											position,
											shownAfterCardBodyExpanded,
											isAuxClick,
										}),
									);
									fireSAINUsageEvent({
										metCriteria: SainAction.RECENT_WORK_CLICKED,
										commonAttributes,
									});
								},
								smartLinksAnalyticsContextLocation:
									SmartLinksAnalyticsContextLocation.SAIN_AGENT_CARD,
							} as unknown as ActivityColumnConfig,
						]
					: []),
			],
			[
				hasTeams,
				teamsData?.teamSearch.nodes,
				handleAgentTeamsViewAllClick,
				profileUrl,
				formatMessage,
				hasRecentActivity,
				handleRecentWorkViewAllClick,
				activityData,
				fireAnalyticsEvent,
				commonAttributes,
				fireSAINUsageEvent,
			],
		);

		return (
			<KnowledgeCardBody
				columnsConfig={columnsConfig}
				profileUrl={profileUrl}
				footerButtonText={formatMessage(messages.knowledge_cards_view_agent)}
				footerButtonAction={SainAction.VIEW_PROFILE_CLICKED}
				testId="agent-card-body"
			/>
		);
	},
);
